// ============================================================
// SheetMasthead - the head of the sheet: who this is, the search that
// narrows the sheet, and the Changed toggle.
// ============================================================
import type { SheetEditor } from '../../hooks/useSheetEditor';
import type { Target } from '../../types';
import clsx from '../../utils/clsx';
import styles from './Sheet.module.css';

interface Props {
  /** In the library editor: the search alone. */
  library: boolean;
  target: Target;
  editor: SheetEditor;
  disabled: boolean;
  query: string;
  onQuery: (query: string) => void;
  showChanged: boolean;
  onShowChanged: (show: boolean) => void;
  onTargetChange: (next: Target) => void;
}

export default function SheetMasthead(props: Props) {
  const { library, target, editor, disabled, query, onQuery, showChanged, onShowChanged, onTargetChange } = props;
  const differing = editor.changes.length + editor.pins.length;
  return (
    <>
      {!library && <Identity target={target} editor={editor} disabled={disabled} onTargetChange={onTargetChange} />}
      <div className={styles.mastRow}>
        <input
          type="search"
          className={styles.pickerSearch}
          data-testid="sheet-search"
          aria-label="Find on the sheet"
          placeholder="Find a field, talent or skill"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
        />
        {!library && (
          <button
            type="button"
            className={clsx(styles.changedToggle, showChanged && styles.changedToggleOn)}
            data-testid="sheet-changed-toggle"
            aria-pressed={showChanged}
            onClick={() => onShowChanged(!showChanged)}
          >
            CHANGED {differing}
          </button>
        )}
      </div>
      {editor.flash && (
        <div className={styles.flash} role="status" data-testid="sheet-flash">
          {editor.flash.removed.join(', ')} removed by {editor.flash.traitLabel}
        </div>
      )}
    </>
  );
}

/** Who this is and where the sheet came from. */
function Identity({
  target,
  editor,
  disabled,
  onTargetChange,
}: Pick<Props, 'target' | 'editor' | 'disabled' | 'onTargetChange'>) {
  return (
    <div className={styles.mastRow}>
      <label className={styles.nameRow}>
        <span className={styles.readoutLabel}>Name</span>
        <input
          className={styles.nameInput}
          value={target.name}
          disabled={disabled}
          onChange={(e) => onTargetChange({ ...target, name: e.target.value })}
        />
      </label>
      <span className={styles.origin} data-testid="sheet-origin">
        {editor.hasOriginal ? 'from library' : (editor.combatant?.kind ?? '')}
      </span>
    </div>
  );
}
